import React, { useState, useEffect } from 'react';
import type { DreamFormData, AnalysisResults } from '../types/frontend';

interface DevModePanelProps {
  onAnalyze: (formData: DreamFormData) => Promise<void>;
  analysisResults: AnalysisResults | null;
  onReset: () => void;
  isAnalyzing: boolean;
}

interface UserDream {
  dream_id: string;
  dream: string;
  context?: any;
  profile_id?: string;
}

interface TrainingFeedback {
  dreamId: string;
  ruleId: string;
  dreamText: string;
  ruleIf: string;
  ruleThen: string;
  feedback: 'relevant' | 'irrelevant' | 'unsure';
  similarity: number;
  timestamp: string;
}

export const DevModePanel: React.FC<DevModePanelProps> = ({
  onAnalyze,
  analysisResults,
  onReset,
  isAnalyzing,
}) => {
  const [userDreams, setUserDreams] = useState<UserDream[]>([]);
  const [currentDreamIndex, setCurrentDreamIndex] = useState(0);
  const [feedbackData, setFeedbackData] = useState<TrainingFeedback[]>([]);
  const [isLoadingDreams, setIsLoadingDreams] = useState(true);

  // Load user dreams on mount
  useEffect(() => {
    loadUserDreams();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadUserDreams = async () => {
    try {
      const response = await fetch('/data/encyclopedia.json');
      const entries = await response.json();
      // Filter to dream narrative entries for HITL testing
      const dreamEntries = entries.filter((e: any) => e.category === 'dream_narratives');
      console.log(`🗂️ Loaded ${dreamEntries.length} dream narrative entries for dev mode`);
      setUserDreams(
        dreamEntries.map((d: any) => ({
          dream_id: d.id || d.dream_id,
          dream: d.summary || d.search_text || '',
          context: d.context,
          profile_id: d.category || 'encyclopedia',
        }))
      );
      setIsLoadingDreams(false);

      // Auto-analyze the first entry
      if (dreamEntries.length > 0) {
        const first = dreamEntries[0];
        await analyzeCurrentDream({
          dream_id: first.id,
          dream: first.summary || first.search_text || '',
          context: first.context,
          profile_id: first.category || 'encyclopedia',
        });
      }
    } catch (error) {
      console.error('Failed to load dream narratives:', error);
      setIsLoadingDreams(false);
    }
  };

  const analyzeCurrentDream = async (dream?: UserDream) => {
    const targetDream = dream || userDreams[currentDreamIndex];
    if (!targetDream) return;

    const formData: DreamFormData = {
      dreamText: targetDream.dream,
      context: targetDream.context || {
        economicStatus: '',
        socialImportance: '',
        socialPosition: '',
        healthStatus: '',
        concerns: '',
      },
    };

    console.log(
      `🔄 Auto-analyzing dream ${currentDreamIndex + 1}/${userDreams.length}: "${targetDream.dream.slice(0, 60)}..."`
    );
    await onAnalyze(formData);
  };

  const handleFeedback = (ruleId: string, feedback: 'relevant' | 'irrelevant' | 'unsure') => {
    if (!analysisResults || !userDreams[currentDreamIndex]) return;

    const currentDream = userDreams[currentDreamIndex];
    const rule = analysisResults.primaryRules.find(r => r.id === ruleId);
    if (!rule) return;

    const feedbackEntry: TrainingFeedback = {
      dreamId: currentDream.dream_id,
      ruleId: ruleId,
      dreamText: currentDream.dream,
      ruleIf: rule.condition,
      ruleThen: rule.outcome,
      feedback: feedback,
      similarity: rule.confidence,
      timestamp: new Date().toISOString(),
    };

    setFeedbackData(prev => {
      // Remove any existing feedback for this dream-rule pair
      const filtered = prev.filter(
        f => !(f.dreamId === currentDream.dream_id && f.ruleId === ruleId)
      );
      return [...filtered, feedbackEntry];
    });

    console.log(
      `📝 Feedback recorded: ${feedback} for rule ${ruleId} on dream ${currentDream.dream_id}`
    );
  };

  const navigateToDream = async (direction: 'prev' | 'next') => {
    let newIndex = currentDreamIndex;

    if (direction === 'next' && currentDreamIndex < userDreams.length - 1) {
      newIndex = currentDreamIndex + 1;
    } else if (direction === 'prev' && currentDreamIndex > 0) {
      newIndex = currentDreamIndex - 1;
    }

    if (newIndex !== currentDreamIndex) {
      setCurrentDreamIndex(newIndex);
      onReset(); // Clear current results
      await analyzeCurrentDream(userDreams[newIndex]);
    }
  };

  const exportFeedbackData = () => {
    const dataStr = JSON.stringify(feedbackData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `hitl_training_data_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    console.log(`💾 Exported ${feedbackData.length} feedback entries`);
  };

  if (isLoadingDreams) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div>🔄 Loading dreams for dev mode...</div>
      </div>
    );
  }

  if (userDreams.length === 0) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div>❌ No dreams found</div>
      </div>
    );
  }

  const currentDream = userDreams[currentDreamIndex];
  const feedbackForCurrentDream = feedbackData.filter(f => f.dreamId === currentDream.dream_id);

  return (
    <div data-testid="hitl-panel" style={{ padding: '1rem' }}>
      {/* Dream Navigation */}
      <div
        style={{
          background: '#F3F4F6',
          border: '1px solid #D1D5DB',
          borderRadius: '0.5rem',
          padding: '1rem',
          marginBottom: '1.5rem',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
          <button
            onClick={() => navigateToDream('prev')}
            disabled={currentDreamIndex === 0}
            style={{
              padding: '0.5rem 1rem',
              background: currentDreamIndex === 0 ? '#E5E7EB' : '#3B82F6',
              color: currentDreamIndex === 0 ? '#9CA3AF' : 'white',
              border: 'none',
              borderRadius: '0.25rem',
              cursor: currentDreamIndex === 0 ? 'not-allowed' : 'pointer',
            }}
          >
            ← Previous
          </button>

          <span style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
            Dream {currentDreamIndex + 1} of {userDreams.length}
          </span>

          <button
            onClick={() => navigateToDream('next')}
            disabled={currentDreamIndex === userDreams.length - 1}
            style={{
              padding: '0.5rem 1rem',
              background: currentDreamIndex === userDreams.length - 1 ? '#E5E7EB' : '#3B82F6',
              color: currentDreamIndex === userDreams.length - 1 ? '#9CA3AF' : 'white',
              border: 'none',
              borderRadius: '0.25rem',
              cursor: currentDreamIndex === userDreams.length - 1 ? 'not-allowed' : 'pointer',
            }}
          >
            Next →
          </button>

          <div style={{ marginLeft: 'auto' }}>
            <button
              onClick={exportFeedbackData}
              disabled={feedbackData.length === 0}
              style={{
                padding: '0.5rem 1rem',
                background: feedbackData.length === 0 ? '#E5E7EB' : '#10B981',
                color: feedbackData.length === 0 ? '#9CA3AF' : 'white',
                border: 'none',
                borderRadius: '0.25rem',
                cursor: feedbackData.length === 0 ? 'not-allowed' : 'pointer',
              }}
            >
              💾 Export ({feedbackData.length})
            </button>
          </div>
        </div>

        <div
          style={{
            background: 'white',
            padding: '1rem',
            borderRadius: '0.375rem',
            border: '1px solid #E5E7EB',
          }}
        >
          <div style={{ fontSize: '0.875rem', color: '#6B7280', marginBottom: '0.5rem' }}>
            Dream ID: {currentDream.dream_id} | Profile: {currentDream.profile_id || 'Unknown'}
          </div>
          <div style={{ fontStyle: 'italic', lineHeight: '1.5' }}>
            &quot;{currentDream.dream}&quot;
          </div>
        </div>

        {feedbackForCurrentDream.length > 0 && (
          <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#059669' }}>
            ✅ {feedbackForCurrentDream.length} feedback entries recorded for this dream
          </div>
        )}
      </div>

      {/* Analysis Results with Feedback */}
      {isAnalyzing ? (
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <div>🔄 Analyzing dream...</div>
        </div>
      ) : analysisResults ? (
        <div>
          {(() => {
            const validRules = analysisResults.primaryRules.filter(
              rule => rule.outcome && rule.outcome.trim().length > 0
            );
            const filteredCount = analysisResults.primaryRules.length - validRules.length;
            if (filteredCount > 0) {
              console.log(
                `🔍 DevMode: Filtered out ${filteredCount} rules with empty outcomes for feedback collection`
              );
            }
            return null;
          })()}

          <div style={{ fontSize: '1.2rem', fontWeight: 'bold', marginBottom: '1rem' }}>
            📋 Rule Relevance Feedback (
            {
              analysisResults.primaryRules.filter(
                rule => rule.outcome && rule.outcome.trim().length > 0
              ).length
            }{' '}
            rules with outcomes)
          </div>

          <div style={{ display: 'grid', gap: '1rem' }}>
            {analysisResults.primaryRules
              .filter(rule => rule.outcome && rule.outcome.trim().length > 0)
              .map((rule, index) => {
                const existingFeedback = feedbackForCurrentDream.find(f => f.ruleId === rule.id);

                return (
                  <div
                    key={rule.id}
                    style={{
                      background: 'white',
                      border: existingFeedback
                        ? `2px solid ${
                            existingFeedback.feedback === 'relevant'
                              ? '#10B981'
                              : existingFeedback.feedback === 'irrelevant'
                                ? '#EF4444'
                                : '#F59E0B'
                          }`
                        : '1px solid #E5E7EB',
                      borderRadius: '0.5rem',
                      padding: '1rem',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                      <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#3B82F6' }}>
                        #{index + 1}
                      </div>

                      <div style={{ flex: 1 }}>
                        <div style={{ marginBottom: '0.5rem' }}>
                          <strong>If:</strong> {rule.condition}
                        </div>
                        <div style={{ marginBottom: '0.75rem', color: '#6B7280' }}>
                          <strong>Then:</strong> {rule.outcome}
                        </div>

                        {/* Source Sentence */}
                        <div
                          style={{
                            marginBottom: '0.75rem',
                            padding: '0.5rem',
                            background: '#F3F4F6',
                            borderRadius: '0.25rem',
                            fontSize: '0.875rem',
                          }}
                        >
                          <strong>Source Sentence:</strong>{' '}
                          <em>&quot;{rule.sourceSentence}&quot;</em>
                        </div>

                        {/* Sentiment Analysis */}
                        {rule.outcomeSentiment && rule.outcomeSentiment.sentiment && (
                          <div
                            style={{
                              marginBottom: '0.75rem',
                              padding: '0.5rem',
                              background:
                                rule.outcomeSentiment.sentiment === 'good'
                                  ? '#F0FDF4'
                                  : rule.outcomeSentiment.sentiment === 'bad'
                                    ? '#FEF2F2'
                                    : '#F9FAFB',
                              border: `1px solid ${
                                rule.outcomeSentiment.sentiment === 'good'
                                  ? '#BBF7D0'
                                  : rule.outcomeSentiment.sentiment === 'bad'
                                    ? '#FECACA'
                                    : '#E5E7EB'
                              }`,
                              borderRadius: '0.25rem',
                              fontSize: '0.875rem',
                            }}
                          >
                            <strong>Sentiment Analysis:</strong>
                            <span
                              style={{
                                marginLeft: '0.5rem',
                                color:
                                  rule.outcomeSentiment.sentiment === 'good'
                                    ? '#065F46'
                                    : rule.outcomeSentiment.sentiment === 'bad'
                                      ? '#991B1B'
                                      : '#374151',
                                fontWeight: 'bold',
                              }}
                            >
                              {rule.outcomeSentiment.sentiment.toUpperCase()}
                            </span>
                            {rule.outcomeSentiment.confidence && (
                              <span style={{ marginLeft: '0.5rem', color: '#6B7280' }}>
                                ({(rule.outcomeSentiment.confidence * 100).toFixed(1)}% confidence)
                              </span>
                            )}
                          </div>
                        )}

                        <div
                          style={{ fontSize: '0.875rem', color: '#9CA3AF', marginBottom: '1rem' }}
                        >
                          Confidence: {(rule.confidence * 100).toFixed(1)}% | Collection:{' '}
                          {rule.collection}
                        </div>

                        {/* Feedback Buttons */}
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button
                            onClick={() => handleFeedback(rule.id, 'relevant')}
                            style={{
                              padding: '0.5rem 1rem',
                              background:
                                existingFeedback?.feedback === 'relevant' ? '#10B981' : '#F3F4F6',
                              color:
                                existingFeedback?.feedback === 'relevant' ? 'white' : '#374151',
                              border: '1px solid #D1D5DB',
                              borderRadius: '0.25rem',
                              cursor: 'pointer',
                            }}
                          >
                            👍 Relevant
                          </button>

                          <button
                            onClick={() => handleFeedback(rule.id, 'irrelevant')}
                            style={{
                              padding: '0.5rem 1rem',
                              background:
                                existingFeedback?.feedback === 'irrelevant' ? '#EF4444' : '#F3F4F6',
                              color:
                                existingFeedback?.feedback === 'irrelevant' ? 'white' : '#374151',
                              border: '1px solid #D1D5DB',
                              borderRadius: '0.25rem',
                              cursor: 'pointer',
                            }}
                          >
                            👎 Irrelevant
                          </button>

                          <button
                            onClick={() => handleFeedback(rule.id, 'unsure')}
                            style={{
                              padding: '0.5rem 1rem',
                              background:
                                existingFeedback?.feedback === 'unsure' ? '#F59E0B' : '#F3F4F6',
                              color: existingFeedback?.feedback === 'unsure' ? 'white' : '#374151',
                              border: '1px solid #D1D5DB',
                              borderRadius: '0.25rem',
                              cursor: 'pointer',
                            }}
                          >
                            🤷 Unsure
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      ) : (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#6B7280' }}>
          No analysis results yet
        </div>
      )}
    </div>
  );
};
